import { useState } from 'react';
import { reportReasons } from '@/mocks/superAdminReports';
import type { ReportType } from '@/mocks/superAdminReports';

interface ReportButtonProps {
  contentType: ReportType;
  contentLabel: string;
  contentPreview: string;
  contentUrl?: string;
  radioName?: string;
  userName?: string;
  variant?: 'icon' | 'text' | 'subtle';
  className?: string;
}

export default function ReportButton({
  contentType,
  contentLabel,
  contentPreview,
  contentUrl,
  radioName,
  userName,
  variant = 'subtle',
  className = '',
}: ReportButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const maxChars = 500;

  const typeLabels: Record<ReportType, { label: string; icon: string }> = {
    radio: { label: 'Radio', icon: 'ri-radio-line' },
    publicacion: { label: 'Publicación', icon: 'ri-file-text-line' },
    comentario: { label: 'Comentario', icon: 'ri-chat-1-line' },
    chat: { label: 'Chat', icon: 'ri-message-2-line' },
    video: { label: 'Video', icon: 'ri-video-line' },
    live: { label: 'Live', icon: 'ri-live-line' },
  };

  const typeInfo = typeLabels[contentType];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return;
    setSubmitted(true);
    setTimeout(() => {
      setIsOpen(false);
      setSubmitted(false);
      setReason('');
      setDescription('');
      setCharCount(0);
    }, 2000);
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    if (val.length <= maxChars) {
      setDescription(val);
      setCharCount(val.length);
    }
  };

  const buttonContent = () => {
    switch (variant) {
      case 'icon':
        return <i className="ri-flag-line text-base"></i>;
      case 'text':
        return (
          <>
            <i className="ri-flag-line"></i>
            <span>Reportar</span>
          </>
        );
      case 'subtle':
      default:
        return (
          <>
            <i className="ri-flag-line text-sm"></i>
            <span className="text-xs">Reportar</span>
          </>
        );
    }
  };

  const buttonClasses = () => {
    const base = 'cursor-pointer transition-colors whitespace-nowrap inline-flex items-center gap-1.5';
    switch (variant) {
      case 'icon':
        return `${base} w-8 h-8 rounded-full hover:bg-foreground-100 justify-center text-foreground-400 hover:text-red-500`;
      case 'text':
        return `${base} px-3 py-2 rounded-lg text-sm font-medium text-foreground-500 hover:text-red-500 hover:bg-red-50`;
      case 'subtle':
      default:
        return `${base} px-2 py-1 rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50`;
    }
  };

  return (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setIsOpen(true);
        }}
        className={`${buttonClasses()} ${className}`}
        title={`Reportar ${typeInfo.label}`}
      >
        {buttonContent()}
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          onClick={() => setIsOpen(false)}
        >
          <div className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm"></div>
          <div
            className="relative w-full max-w-lg bg-background-50 rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-background-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                  <i className="ri-flag-fill text-red-500 text-lg"></i>
                </div>
                <div>
                  <h3 className="font-bold text-foreground-950 text-sm">Reportar contenido</h3>
                  <p className="text-foreground-400 text-xs flex items-center gap-1.5">
                    <i className={`${typeInfo.icon} text-[10px]`}></i>
                    {typeInfo.label}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 rounded-full bg-foreground-100 hover:bg-foreground-200 flex items-center justify-center cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            {/* Content Preview */}
            <div className="px-5 py-3 bg-background-100/60 border-b border-background-200/70">
              <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider mb-1.5">Contenido reportado</p>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-foreground-200 flex items-center justify-center shrink-0 mt-0.5">
                  <i className={`${typeInfo.icon} text-foreground-500 text-sm`}></i>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-foreground-800">{contentLabel}</p>
                  <p className="text-xs text-foreground-500 mt-0.5 line-clamp-2">{contentPreview}</p>
                  {radioName && (
                    <p className="text-[10px] text-foreground-400 mt-1 flex items-center gap-1">
                      <i className="ri-radio-line"></i>
                      {radioName}
                    </p>
                  )}
                  {userName && (
                    <p className="text-[10px] text-foreground-400 mt-0.5 flex items-center gap-1">
                      <i className="ri-user-line"></i>
                      {userName}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {!submitted ? (
              <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
                {/* Reason */}
                <div>
                  <label className="text-xs font-semibold text-foreground-700 mb-1.5 block">
                    Motivo del reporte <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    required
                    className="w-full px-3.5 py-2.5 bg-background-100 border border-background-200 rounded-xl text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors cursor-pointer"
                  >
                    <option value="" disabled>Selecciona un motivo...</option>
                    {reportReasons.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="text-xs font-semibold text-foreground-700 mb-1.5 block">
                    Descripción adicional
                  </label>
                  <textarea
                    value={description}
                    onChange={handleDescriptionChange}
                    rows={3}
                    maxLength={maxChars}
                    placeholder="Describe lo que sucede y por qué este contenido debería ser revisado..."
                    className="w-full px-3.5 py-2.5 bg-background-100 border border-background-200 rounded-xl text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                  ></textarea>
                  <p className={`text-[10px] mt-1 text-right ${charCount > maxChars * 0.9 ? 'text-red-500' : 'text-foreground-400'}`}>
                    {charCount}/{maxChars}
                  </p>
                </div>

                {/* Info */}
                <div className="flex items-start gap-2 px-3 py-2.5 bg-secondary-50 rounded-xl border border-secondary-200/60">
                  <i className="ri-information-line text-secondary-500 text-sm mt-0.5 shrink-0"></i>
                  <p className="text-[11px] text-secondary-700 leading-relaxed">
                    Los reportes son revisados por nuestro equipo de moderación. No se compartirá tu identidad con la radio reportada.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-sm font-medium text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer text-center"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={!reason.trim()}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 disabled:bg-foreground-200 disabled:cursor-not-allowed text-sm font-semibold text-background-50 transition-colors cursor-pointer text-center flex items-center justify-center gap-2"
                  >
                    <i className="ri-flag-fill text-sm"></i>
                    Enviar reporte
                  </button>
                </div>
              </form>
            ) : (
              <div className="px-5 py-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                  <i className="ri-check-line text-green-500 text-2xl"></i>
                </div>
                <h4 className="font-bold text-foreground-950 text-base mb-1">Reporte enviado</h4>
                <p className="text-foreground-500 text-sm">
                  Gracias por ayudarnos a mantener la plataforma segura. Revisaremos tu reporte lo antes posible.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}